import axios from "axios"
import config from "~/config";
import httpCode from "~/constants/httpCode";
import { logoutUserOk } from "~/store/actions";
import {
  AccessDeniedException,
  NotFoundException,
  BadRequestException,
  ValidationException,
  AuthException,
  ServerErrorException,
  ACC_TOKEN_MISSING,
  ACC_TOKEN_JWT_EXPIRED,
  NetworkErrorException,
  AuthLockedException,
  REF_TOKEN_NOT_FOUND,
  REF_TOKEN_EXPIRED,
} from "./errorHelper";
import { withAuth } from "./utilHelper";
import { AppStore } from "~/store";

const axiosApi = axios.create({
  baseURL: config.API_BE_URL as string,
  withCredentials: true,
});

// TEMPORARY add tabId to the request headers
axiosApi.interceptors.request.use(
  config => {
    config.headers['X-Tab-Id'] = window.name;
    config.headers['X-App-Id'] = 'SSO';
    config.headers['X-Sess-Id'] = document.cookie.split('; ').find(row => row.startsWith('sessionId='))?.split('=')[1];
    return config;
  },
);

const refreshEndpoint = withAuth('/refresh');
let isRefreshing = false;
let refreshQueue: any[] = [];
let store: AppStore;

axiosApi.interceptors.response.use(
  response => response,
  error => {
    if (!!error.response) {
      // see if this error is because the access token expired
      // and therefore we should try to refresh it
      if (shouldRefreshAccessToken(error)) {
        return handleAccessTokenRefresh(error);
      }
      switch (error.response.status) {
        case httpCode.unauthorized:
          if (error.response.data.lockedUntil) {
            error = new AuthLockedException(undefined, error.response.data.code, error.response.data.lockedUntil);
          } else {
            error = new AuthException(undefined, error.response.data.code);
          }
          break;
        case httpCode.forbidden:
          error = new AccessDeniedException(undefined, error.response.data.code);
          break;
        case httpCode.notFound:
          error = new NotFoundException(undefined, error.response.data.code);
          break;
        case httpCode.badRequest:
          if (error.response.data.fields) {
            error = new ValidationException(undefined, error.response.data.fields);
          } else {
            error = new BadRequestException(undefined, error.response.data.code);
          }
          break;
        case httpCode.internalServerError:
          error = new ServerErrorException(undefined, error.response.data.code);
          break;
      }
    } else if (!!error.request) {
      // we have a request but not a response
      // so this is most likely a network problem
      error = new NetworkErrorException();
    }
    return Promise.reject(error);
  }
);

export const get = <Type>(url: string, config = {}) => axiosApi
  .get<Type>(url, config)
  .then(response => response.data);

export const post = <Type>(url: string, data: any, config = {}) => axiosApi
  .post<Type>(url, data, config)
  .then(response => response.data);

export const put = <Type>(url: string, data: any, config = {}) => axiosApi
  .put<Type>(url, data, config)
  .then(response => response.data);

export const del = <Type>(url: string, config = {}) => axiosApi
  .delete<Type>(url, config)
  .then(response => response.data);

export const injectStore = (_store: AppStore) => {
  store = _store
}

export const refreshAccessToken = (previousRequest: any, isRetry = false) => post(refreshEndpoint, {}, { headers: { 'X-Orig-Req': previousRequest } })
  .catch(ex => {
    const isRetryable = ![REF_TOKEN_NOT_FOUND, REF_TOKEN_EXPIRED].includes(ex.response?.data?.code);
    // there are a few scenarios where we know for sure that a retry is pointless
    // for all the rest, we want to try again before deciding that the session is unrecoverable
    if (!isRetry && isRetryable) {
      // wait a brief moment before retrying
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          refreshAccessToken(previousRequest, true)
            .then(resolve)
            .catch(reject);
        }, 100);
      });
    }
    // not retryable
    throw ex;
  });

const shouldRefreshAccessToken = (error: any) => {
  const originalRequest = error.config;
  return !!error.response // server error (not client)
    && error.response.status === httpCode.unauthorized // authentication error
    && [ACC_TOKEN_MISSING, ACC_TOKEN_JWT_EXPIRED].includes(error.response.data.code) // specific error codes
    && originalRequest.url != refreshEndpoint // not the refresh request itself
    && !originalRequest._retry; // not a retry request
}

const handleAccessTokenRefresh = (error: any) => {
  // check if a refresh call is already in progress
  // we must not allow multiple concurrent refresh calls
  // to avoid race conditions
  if (!isRefreshing) {
    // this refresh call is starting so block other refresh calls
    isRefreshing = true;
    // fire the refresh call
    refreshAccessToken(error.config.url)
      // on refresh success
      .then(() => {
        // now that we have a new access token let's retry all queued requests
        refreshQueue.forEach(item => item.resolve());
      })
      // on refresh error
      .catch(() => {
        // we failed to aquire a new access token so let's abort all queued requests
        refreshQueue.forEach(item => item.reject());
        // logout and redirect to login
        store.dispatch(logoutUserOk());
      })
      .finally(() => {
        // this refresh call is done so allow other refresh calls
        isRefreshing = false;
        // empty the request queue
        refreshQueue = [];
      });
  }
  // multiple concurrent requests might try to refresh the token at the same time
  // we want to do 2 things in respect to this:
  // 1. allow a single refresh call at a time to avoid race conditions (see 'isRefreshing' above)
  // 2. queue all the requests that occur during a refresh call so they can be retried later (see below)
  return new Promise((resolve, reject) => {
    refreshQueue.push({
      // callback to be called after the token is refreshed
      // retries the request
      resolve: () => {
        // get the original request from the error
        const originalRequest = error.config;
        // mark the request as being a retry
        // to avoid being retried itself
        originalRequest._retry = true;
        // fire the retry request
        resolve(axiosApi.request(originalRequest));
      },
      // callback to be called after the token has failed to refresh
      // aborts the request
      reject: () => {
        reject(error);
      },
    })
  });
}